import { apiClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

/** A single enrollable product returned by the platform catalog API. */
export interface CatalogProduct {
  code: string;
  name: string;
  /** true → contract product: enabling requires contractType + dates. false → toggle-only: just flip enabled, no contract fields. */
  supportsContract: boolean;
  description: string;
}

/** A named group of products returned by GET /products/catalog. */
export interface CatalogCategory {
  code: string;
  name: string;
  /** Sort order for rendering categories top-to-bottom. */
  order: number;
  products: CatalogProduct[];
}

/** Fetches the platform product catalog (GET /products/catalog). */
export async function getProductCatalog(): Promise<CatalogCategory[]> {
  return apiClient.get(endpoints.products.catalog()).json<CatalogCategory[]>();
}

/** An enrolled product as returned by GET /subsidiaries/:id/products. */
export interface SubsidiaryProduct {
  id: string;
  subsidiaryId: string;
  productCode: string;
  categoryCode: string;
  enabled: boolean;
  contractType: 'TRIAL_POC' | 'LIVE' | null;
  pocStartDate: string | null;
  pocEndDate: string | null;
  contractEndDate: string | null;
  linkedMsaSfdcOpportunity: string | null;
  notes: string | null;
  pointOfContactEmails: string[];
  details: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

/** Fetches the enrolled products for a subsidiary (GET /subsidiaries/:id/products). */
export async function getSubsidiaryProducts(subsidiaryId: string): Promise<SubsidiaryProduct[]> {
  return apiClient.get(endpoints.subsidiaries.products(subsidiaryId)).json<SubsidiaryProduct[]>();
}

/**
 * Partial-update payload for PATCH /subsidiaries/:id/products/:productCode.
 * Only the fields present in the payload are written; everything else is preserved.
 */
export interface UpdateSubsidiaryProductPayload {
  enabled?: boolean;
  contractType?: 'TRIAL_POC' | 'LIVE';
  pocStartDate?: string | null;
  pocEndDate?: string | null;
  contractEndDate?: string | null;
  linkedMsaSfdcOpportunity?: string | null;
  notes?: string | null;
}

/**
 * Patches contract details for a single enrolled product.
 * PATCH /subsidiaries/:subsidiaryId/products/:productCode
 */
export async function updateSubsidiaryProduct(
  subsidiaryId: string,
  productCode: string,
  payload: UpdateSubsidiaryProductPayload,
): Promise<SubsidiaryProduct> {
  return apiClient
    .patch(endpoints.subsidiaries.updateProduct(subsidiaryId, productCode), { json: payload })
    .json<SubsidiaryProduct>();
}
